/**
 * Risk #3 hit panel — CANON: fill RiskHitPanelLayout slots only.
 * Do not add a custom card shell. See `./riskCardLayoutCanon.js`.
 */
import { Link } from 'react-router-dom';
import { resolveSupportActionLabel } from '../constants/supportActionLabels';
import { buildDeviceHistoryPath } from '../pages/deviceHistoryPath';
import { formatDateTime } from './RiskCard';
import Risk3SignalFeature from './Risk3SignalFeature';
import RiskHitPanelLayout, {
  RISK_HIT_LAYOUT,
  RiskHitMetricGrid,
} from './RiskHitPanelLayout';

const RADIO_LABELS = {
  wifi: 'Wi‑Fi',
  nbiot: 'NB‑IoT',
};

const METRIC_HINTS = {
  lastSignal: 'Последний измеренный уровень радиосигнала устройства.',
  exposure: 'Насколько долго и сильно сигнал был слабым — накопленный вклад в скор.',
  samples: 'Сколько замеров сигнала учтено в формуле риска.',
};

function formatPeriod(seconds) {
  const hours = Number(seconds || 0) / 3600;
  return hours >= 24 ? `${Number((hours / 24).toFixed(1))} дн.` : `${Math.round(hours)} ч.`;
}

/** Risk #3 — content only; shell is RiskHitPanelLayout. */
export default function Risk3HitPanel({ hit, onOpenHistory }) {
  const { facts, features, rule_result: ruleResult } = hit;
  const riskScore =
    ruleResult.risk_score == null || !Number.isFinite(Number(ruleResult.risk_score))
      ? null
      : Number(ruleResult.risk_score);
  const riskLevel = ruleResult.risk_level ?? (riskScore == null ? 'unknown' : ruleResult.risk_level);
  const serial = facts.serial_number || 'Без serial';
  const deviceId = facts.device_id;

  const criticalRows = [
    {
      key: 'action',
      label: 'Действие',
      value: resolveSupportActionLabel(ruleResult),
    },
    {
      key: 'last-signal',
      label: 'Последний сигнал',
      hint: METRIC_HINTS.lastSignal,
      value: `${features.last_signal_dbm} дБм`,
      numeric: true,
    },
    {
      key: 'exposure',
      label: 'Накопленная тяжесть',
      hint: METRIC_HINTS.exposure,
      value: `${(Number(features.signal_exposure) * 100).toFixed(1)}%`,
      numeric: true,
    },
    {
      key: 'samples',
      label: 'Учтено замеров',
      hint: METRIC_HINTS.samples,
      value: features.sample_count ?? 0,
      numeric: true,
    },
  ];

  const detailRows = [
    {
      key: 'period',
      label: 'Период выхода',
      value: formatPeriod(features.period_seconds),
      numeric: true,
    },
    {
      key: 'memory',
      label: 'Память формулы',
      value: formatPeriod(Number(features.period_seconds) * Number(features.memory_periods || 8)),
      numeric: true,
    },
    {
      key: 'last-at',
      label: 'Последний замер',
      value: formatDateTime(features.last_signal_at),
    },
  ];

  return (
    <RiskHitPanelLayout
      embedded
      riskNumber={3}
      badges={
        <>
          <span className="inline-flex shrink-0 rounded-full border border-slate-300 dark:border-slate-600/50 bg-slate-100 dark:bg-slate-800/80 px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">
            Контроллер
          </span>
          <span className="inline-flex shrink-0 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-xs font-medium text-cyan-700 dark:text-cyan-200">
            {RADIO_LABELS[features.radio] || features.radio}
          </span>
        </>
      }
      title={serial}
      scoreDisplay={riskScore == null ? '—' : riskScore.toFixed(1)}
      riskLevel={riskLevel}
      scoreBarValue={riskScore ?? 0}
      feature={
        <Risk3SignalFeature
          dbm={features.last_signal_dbm}
          radio={features.radio}
          lastAtLabel={formatDateTime(features.last_signal_at)}
        />
      }
      metrics={<RiskHitMetricGrid rows={criticalRows} />}
      details={
        <>
          <RiskHitMetricGrid rows={detailRows} />
          <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
            Скор растёт плавно: чем слабее сигнал и чем дольше он держится, тем выше риск. Единичный
            провал и отсутствие телеметрии сами по себе риск не повышают.
          </p>
          {ruleResult.reason_codes?.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs text-slate-500">Причины решения</p>
              <div className="flex flex-wrap gap-1.5">
                {ruleResult.reason_codes.map((code) => (
                  <span
                    key={code}
                    className="rounded-full bg-white px-2 py-1 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400"
                  >
                    {code}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      }
      actions={
        deviceId != null ? (
          <>
            {onOpenHistory && (
              <button
                type="button"
                onClick={() => onOpenHistory({ ...facts, risk_number: 3 })}
                className={`${RISK_HIT_LAYOUT.actionBtn} ${RISK_HIT_LAYOUT.actionHistory}`}
              >
                История рисков
              </button>
            )}
            <Link
              to={buildDeviceHistoryPath(deviceId, { serialNumber: serial })}
              className={`${RISK_HIT_LAYOUT.actionBtn} ${RISK_HIT_LAYOUT.actionSecondary}`}
            >
              График сигнала
            </Link>
          </>
        ) : null
      }
    />
  );
}
